import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import ListFilterBar from "@/components/ListFilterBar";
import Pagination from "@/components/Pagination";
import { usePagination } from "@/hooks/usePagination";
import {
  isWithinDateRange,
  useClearFilterParams,
  useFilterParam,
} from "@/hooks/useFilterParam";
import { Label } from "@/components/ui/label";
import { formatDate } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// The DB column `customers.contact_type` was added in migration 0018. We
// keep the same `Customer` TypeScript shape so call sites that still know
// the row as a "customer" don't break — only the UI labels rename to
// "Entity" / "Entities".
type ContactType = "customer" | "estimator" | "attention";

const CONTACT_TYPE_OPTIONS: { value: ContactType; label: string }[] = [
  { value: "customer", label: "Customer" },
  { value: "estimator", label: "Estimator" },
  { value: "attention", label: "Attention" },
];

const FILTER_KEYS = ["q", "contact_type", "date_from", "date_to"] as const;

interface Customer {
  id: number;
  name: string;
  contact_type: ContactType;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  created_at?: string | null;
  isActive?: boolean;
}

interface EditableEntity {
  id?: number;
  name: string;
  contact_type: ContactType;
  phone: string;
}

const emptyEditable: EditableEntity = {
  name: "",
  contact_type: "customer",
  phone: "",
};

function contactTypeLabel(value: string): string {
  const match = CONTACT_TYPE_OPTIONS.find((o) => o.value === value);
  return match ? match.label : "Customer";
}

// Semantic palette per role so light + dark mode stay legible without
// reaching for raw HEX values.
function contactTypeBadgeClass(value: string): string {
  switch (value) {
    case "estimator":
      return "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30";
    case "attention":
      return "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30";
    case "customer":
    default:
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30";
  }
}

function normalizeContactType(value: unknown): ContactType {
  const lowered = String(value ?? "customer").toLowerCase();
  if (lowered === "estimator" || lowered === "attention") return lowered;
  return "customer";
}

export default function Customers() {
  const { can } = useAuth();
  const writable = can("customers.write");
  // The existing customers.delete permission is repurposed as the
  // "can disable / enable" gate (the trash action became a non-destructive
  // Disable/Enable toggle).
  const canToggleActive = can("customers.delete");

  const [rows, setRows] = useState<Customer[]>([]);
  const [search, setSearch] = useFilterParam("q");
  const [contactTypeFilter, setContactTypeFilter] = useFilterParam("contact_type");
  const [dateFrom, setDateFrom] = useFilterParam("date_from");
  const [dateTo, setDateTo] = useFilterParam("date_to");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EditableEntity | null>(null);
  const [saving, setSaving] = useState(false);
  const clearFilters = useClearFilterParams(FILTER_KEYS);

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    const typeNeedle =
      contactTypeFilter && contactTypeFilter !== "all"
        ? contactTypeFilter.toLowerCase()
        : "";
    return rows.filter((r) => {
      const searchMatch =
        !s ||
        r.name.toLowerCase().includes(s) ||
        (r.phone ?? "").toLowerCase().includes(s) ||
        (r.email ?? "").toLowerCase().includes(s);
      const typeMatch = !typeNeedle || r.contact_type === typeNeedle;
      const dateMatch = isWithinDateRange(r.created_at, dateFrom, dateTo);
      return searchMatch && typeMatch && dateMatch;
    });
  }, [rows, search, contactTypeFilter, dateFrom, dateTo]);

  const hasAnyFilter = Boolean(
    search ||
      (contactTypeFilter && contactTypeFilter !== "all") ||
      dateFrom ||
      dateTo,
  );

  const pg = usePagination(filtered);

  useEffect(() => {
    pg.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, contactTypeFilter, dateFrom, dateTo]);

  async function load() {
    setLoading(true);
    try {
      // Inactive entities are hidden by default; the "Show inactive" toggle
      // opts into them via the API's includeInactive param.
      const path = showInactive
        ? "/customers?includeInactive=1"
        : "/customers";
      const res = await api.get<{ customers: Customer[] }>(path);
      setRows(
        res.customers.map((r) => ({
          ...r,
          contact_type: normalizeContactType(r.contact_type),
          isActive: r.isActive !== false,
        })),
      );
    } catch (err) {
      toast.error((err as Error).message || "Failed to load entities");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const openNew = () => {
    setEditing({ ...emptyEditable });
    setOpen(true);
  };

  const openEdit = (c: Customer) => {
    setEditing({
      id: c.id,
      name: c.name,
      contact_type: c.contact_type,
      phone: c.phone ?? "",
    });
    setOpen(true);
  };

  async function save() {
    if (!editing) return;
    if (!String(editing.name || "").trim())
      return toast.error("Name is required");
    setSaving(true);
    try {
      // The DB columns email/address/notes still exist (kept nullable for
      // backward compatibility). The new Entity form simply omits them.
      const payload = {
        name: editing.name.trim(),
        contact_type: editing.contact_type,
        phone: editing.phone.trim() || null,
      };
      if (editing.id) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Entity updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Entity created");
      }
      setOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Non-destructive disable / enable. Disabling routes through DELETE (which
  // the API now soft-disables); enabling sends is_active back to 1 via PUT.
  // Both are gated by the customers.delete permission in the UI.
  async function toggleActive(c: Customer) {
    const nextActive = c.isActive === false;
    if (!nextActive && !confirm(`Disable entity "${c.name}"?`)) return;
    setRows((prev) =>
      prev.map((r) => (r.id === c.id ? { ...r, isActive: nextActive } : r)),
    );
    try {
      if (nextActive) {
        await api.put(`/customers/${c.id}`, { is_active: 1 });
        toast.success("Entity enabled");
      } else {
        await api.del(`/customers/${c.id}`);
        toast.success("Entity disabled");
      }
      await load();
    } catch (err) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === c.id ? { ...r, isActive: c.isActive } : r,
        ),
      );
      toast.error((err as Error).message || "Update failed");
    }
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Entities</h1>
          <p className="text-muted-foreground">
            Manage your customers, estimators, and attention contacts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <Checkbox
              checked={showInactive}
              onCheckedChange={(v) => setShowInactive(v === true)}
            />
            Show inactive
          </label>
          {writable ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-gradient-primary">
                <Plus className="h-4 w-4 mr-2" />
                New Entity
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing?.id ? "Edit Entity" : "New Entity"}
                </DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <div className="space-y-1.5">
                  <Label>Name *</Label>
                  <Input
                    value={editing?.name ?? ""}
                    onChange={(e) =>
                      setEditing((f) =>
                        f ? { ...f, name: e.target.value } : f,
                      )
                    }
                    placeholder="e.g. Acme Holdings Ltd."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Contact Type *</Label>
                  <Select
                    value={editing?.contact_type ?? "customer"}
                    onValueChange={(value) =>
                      setEditing((f) =>
                        f
                          ? { ...f, contact_type: normalizeContactType(value) }
                          : f,
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input
                    value={editing?.phone ?? ""}
                    onChange={(e) =>
                      setEditing((f) =>
                        f ? { ...f, phone: e.target.value } : f,
                      )
                    }
                    placeholder="e.g. +233 20 000 0000"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  className="bg-gradient-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
        <ListFilterBar
          title="Search Entity"
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search entities..."
          status={{
            value: contactTypeFilter || "all",
            onChange: (next) =>
              setContactTypeFilter(next === "all" ? "" : next),
            placeholder: "Contact Type",
            options: [
              { value: "all", label: "All contact types" },
              ...CONTACT_TYPE_OPTIONS.map((opt) => ({
                value: opt.value,
                label: opt.label,
              })),
            ],
          }}
          dateFrom={{ value: dateFrom, onChange: setDateFrom }}
          dateTo={{ value: dateTo, onChange: setDateTo }}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
        <Card className="shadow-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Contact Type</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-foreground py-10"
                  >
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center text-muted-foreground py-10"
                  >
                    No entities.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                pg.pageItems.map((c, i) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-muted-foreground">
                      {pg.from + i}
                    </TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${contactTypeBadgeClass(
                          c.contact_type,
                        )}`}
                      >
                        {contactTypeLabel(c.contact_type)}
                      </span>
                    </TableCell>
                    <TableCell>{c.phone || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.created_at ? formatDate(c.created_at) : "—"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          c.isActive !== false
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                            : "border-muted-foreground/30 bg-muted text-muted-foreground"
                        }
                      >
                        {c.isActive !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1 justify-end">
                        {writable ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(c)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4 text-primary" />
                          </Button>
                        ) : null}
                        {canToggleActive ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => toggleActive(c)}
                            title={
                              c.isActive !== false ? "Disable" : "Enable"
                            }
                          >
                            {c.isActive !== false ? (
                              <PowerOff className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                            ) : (
                              <Power className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {!loading && filtered.length > 0 ? (
            <Pagination
              page={pg.page}
              pageCount={pg.pageCount}
              total={pg.total}
              from={pg.from}
              to={pg.to}
              onPrev={pg.prev}
              onNext={pg.next}
            />
          ) : null}
        </Card>
      </div>
    </div>
  );
}
